import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { ImagePlus, Loader2 } from "lucide-react";
import { api, errMsg, uploadFile, useApi, media } from "@/lib/api";
import { Page, Spinner } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { title: "", description: "", location_name: "", address: "", refreshments: "", start_time: "", end_time: "", price: "10", min_attendees: "", max_attendees: "", visibility: "everyone", checkin_required: false, flyer: "" };
const toLocal = (s) => { try { return format(new Date(s), "yyyy-MM-dd'T'HH:mm"); } catch { return ""; } };
const F = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
const num = (v) => (v === "" || v == null ? null : Number(v));

export default function EventForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, isLoading } = useApi(id ? `/events/${id}` : null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    const e = data?.event;
    if (e) setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, e[k] ?? EMPTY[k]])), start_time: toLocal(e.start_time), end_time: toLocal(e.end_time), price: String(e.price ?? "") });
  }, [data]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onFlyer = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const url = await uploadFile(file); setF((p) => ({ ...p, flyer: url })); } catch (err) { toast.error(errMsg(err)); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (new Date(f.end_time) <= new Date(f.start_time)) return toast.error("End time must be after start time");
    setBusy(true);
    const body = {
      title: f.title, description: f.description || null, location_name: f.location_name, address: f.address || null,
      refreshments: f.refreshments || null, start_time: new Date(f.start_time).toISOString(), end_time: new Date(f.end_time).toISOString(),
      price: Number(f.price), min_attendees: num(f.min_attendees), max_attendees: num(f.max_attendees),
      visibility: f.visibility, checkin_required: f.checkin_required,
    };
    try {
      if (id) { await api.patch(`/events/${id}`, body); toast.success("Event updated"); nav(`/manage/${id}`); }
      else {
        const { data: r } = await api.post("/events", { ...body, flyer: f.flyer || null, invited_user_ids: [], early_access: false, early_access_hours: 0 });
        toast.success("Event published");
        nav(`/events/${r.event?.id || r.id}`);
      }
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };

  if (id && isLoading) return <Page><Spinner /></Page>;
  return (
    <Page className="max-w-3xl">
      <p className="font-mono text-xs uppercase tracking-widest text-stone-500">{id ? "Edit" : "Host"}</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">{id ? "Edit event" : "Host an event"}</h1>
      <form onSubmit={submit} className="mt-10 space-y-6 p-6 sm:p-8 rounded-3xl bg-white border border-stone-200">
        {!id && (
          <label data-testid="event-flyer-upload" className="cursor-pointer block aspect-[16/9] rounded-2xl border-2 border-dashed border-stone-300 hover:border-[#FF5C00] overflow-hidden bg-stone-50 transition-colors">
            {f.flyer ? <img src={media(f.flyer)} alt="Flyer" className="h-full w-full object-cover" /> : (
              <div className="h-full grid place-items-center text-stone-500 text-sm"><span className="flex items-center gap-2">{uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />} Upload a flyer (optional)</span></div>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={onFlyer} data-testid="event-flyer-input" />
          </label>
        )}
        <F label="Title"><Input data-testid="event-title-input" value={f.title} onChange={set("title")} required /></F>
        <F label="Description"><Textarea data-testid="event-description-input" value={f.description} onChange={set("description")} rows={4} /></F>
        <div className="grid sm:grid-cols-2 gap-5">
          <F label="Location name"><Input data-testid="event-location-input" value={f.location_name} onChange={set("location_name")} required /></F>
          <F label="Address"><Input data-testid="event-address-input" value={f.address} onChange={set("address")} /></F>
          <F label="Starts"><Input data-testid="event-start-input" type="datetime-local" value={f.start_time} onChange={set("start_time")} required /></F>
          <F label="Ends"><Input data-testid="event-end-input" type="datetime-local" value={f.end_time} onChange={set("end_time")} required /></F>
          <F label="Ticket price ($, min 1)"><Input data-testid="event-price-input" type="number" min="1" step="0.5" value={f.price} onChange={set("price")} required /></F>
          <F label="Refreshments"><Input data-testid="event-refreshments-input" value={f.refreshments} onChange={set("refreshments")} placeholder="Coffee, snacks..." /></F>
          <F label="Min attendees"><Input data-testid="event-min-input" type="number" min="1" value={f.min_attendees} onChange={set("min_attendees")} /></F>
          <F label="Max attendees"><Input data-testid="event-max-input" type="number" min="1" value={f.max_attendees} onChange={set("max_attendees")} /></F>
          <F label="Who can see it">
            <Select value={f.visibility} onValueChange={(v) => setF({ ...f, visibility: v })}>
              <SelectTrigger data-testid="event-visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone" data-testid="visibility-everyone">Everyone</SelectItem>
                <SelectItem value="followers" data-testid="visibility-followers">Followers only</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <div className="flex items-center justify-between rounded-2xl bg-stone-100 px-4 py-3 text-sm self-end">
            <span>Require check-in</span>
            <Switch data-testid="event-checkin-switch" checked={f.checkin_required} onCheckedChange={(v) => setF({ ...f, checkin_required: v })} />
          </div>
        </div>
        <button data-testid="create-event-submit-btn" disabled={busy || uploading} className="h-12 px-8 rounded-full bg-[#FF5C00] hover:bg-[#E04E00] disabled:opacity-60 text-white font-semibold transition-colors">
          {busy ? "Saving..." : id ? "Save changes" : "Publish event"}
        </button>
      </form>
    </Page>
  );
}
